import React, { useEffect, useRef, useMemo } from 'react';
import { sanitize, SanitizationLevel } from 'froala/helpers/sanitizer';

type InstitutionEmailHeaderProps = {
  emailHeader?: string;
  emailHeaderStyle?: string;
};

/**
 * Renders institution's custom email header in an isolated iframe to prevent XSS and style leakage.
 * Sanitizes both HTML and CSS content before rendering.
 */
const InstitutionEmailHeader: React.FC<InstitutionEmailHeaderProps> = ({
  emailHeader,
  emailHeaderStyle,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Sanitize institution email header to prevent XSS
  const sanitizedEmailHeader = useMemo(
    () => (emailHeader ? sanitize(emailHeader, SanitizationLevel.EMAIL_HEADER) : ''),
    [emailHeader]
  );

  // Basic CSS sanitization - strip script tags and dangerous properties
  const sanitizedEmailHeaderStyle = useMemo(() => {
    if (!emailHeaderStyle) return '';
    // Remove script tags, expressions, and dangerous CSS patterns that could enable XSS
    return emailHeaderStyle
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/expression\s*\(/gi, '')
      .replace(/behavior\s*:/gi, '')
      .replace(/@import/gi, '')
      .replace(/url\s*\(/gi, '') // Remove url() which could load external resources or use data: URIs
      .replace(/-moz-binding\s*:/gi, '') // Old Firefox XSS vector
      .replace(/vbscript:/gi, ''); // VBScript protocol
  }, [emailHeaderStyle]);

  useEffect(() => {
    if (iframeRef.current && sanitizedEmailHeader) {
      const handleIframeLoad = () => {
        const iframeDoc = iframeRef.current?.contentDocument;
        if (iframeDoc) {
          // Safely inject CSS using DOM methods after iframe loads
          if (sanitizedEmailHeaderStyle) {
            const styleElement = iframeDoc.createElement('style');
            styleElement.textContent = sanitizedEmailHeaderStyle;
            iframeDoc.head.appendChild(styleElement);
          }

          // Disable link interaction to keep preview stable (prevent navigation)
          const disableLinkStyle = iframeDoc.createElement('style');
          disableLinkStyle.textContent = 'a { pointer-events: none; cursor: default; }';
          iframeDoc.head.appendChild(disableLinkStyle);

          // Auto-size iframe to content height with minimum of 50px
          const resizeIframe = () => {
            if (iframeDoc.body && iframeRef.current) {
              const contentHeight = iframeDoc.body.scrollHeight;
              iframeRef.current.style.height = `${Math.max(contentHeight, 50)}px`;
            }
          };

          // Resize after styles are applied
          setTimeout(resizeIframe, 0);

          // Also resize after images load
          const images = iframeDoc.querySelectorAll('img');
          images.forEach((img) => {
            img.addEventListener('load', resizeIframe);
          });
        }
      };

      const iframe = iframeRef.current;
      iframe.addEventListener('load', handleIframeLoad);

      return () => {
        iframe.removeEventListener('load', handleIframeLoad);
      };
    }
  }, [sanitizedEmailHeader, sanitizedEmailHeaderStyle]);

  if (!sanitizedEmailHeader) {
    return null;
  }

  // Use srcDoc for safer content injection with sandboxing
  const iframeContent = `
    <!DOCTYPE html>
    <html>
      <head></head>
      <body style="margin: 0; padding: 0;">
        ${sanitizedEmailHeader}
      </body>
    </html>
  `;

  return (
    <iframe
      ref={iframeRef}
      style={{ border: 0, width: '100%', display: 'block', minHeight: '50px', overflow: 'hidden' }}
      title='email header preview'
      sandbox='allow-same-origin'
      srcDoc={iframeContent}
    />
  );
};

export default InstitutionEmailHeader;
